// 概要：役割ごとの権限（閲覧・編集/作成・削除）を管理画面で設定するタブ。
// ここで「オンライン中（一覧）」= onlineList の閲覧可否をボタンで切り替え可能にする。

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography, Select, Table, Checkbox, Button, Spin, Space, App } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { db } from "../../../firebase-config";
import { doc, updateDoc, getDocs, collection } from "firebase/firestore";

const { Text } = Typography;

/* ===== 管理対象の機能／タブ一覧 =====
   ※onlineList を追加（オンライン中ユーザー一覧の閲覧権限） */
export const RESOURCES = [
  { key: "dashboard",   name: "ホームページ" },
  { key: "myPage",      name: "マイページ" },
  { key: "level1",      name: "簿記 3級" },
  { key: "level2",      name: "簿記 4級" },
  { key: "level3",      name: "日商簿記" },
  { key: "takeExam",    name: "テスト実施" },
  { key: "tests",       name: "テスト管理" },
  { key: "journalPractise",name: "仕分練習" },
    { key: "setting",     name: "設定管理" },
  { key: "students",    name: "学生管理" },
  { key: "teachers",    name: "教員管理" },
  { key: "roles",       name: "役割管理" },
  { key: "permissions", name: "権限管理" },
  { key: "onlineList",  name: "オンライン中（一覧）" }, // ★ 追加
];

const PermissionManagementTab = ({ roles }) => {
  const { message } = App.useApp();

  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loadingRole, setLoadingRole] = useState(false);
  const [saving, setSaving] = useState(false);

  const [rolesLocal, setRolesLocal] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // 役割のソース（props 優先、なければ Firestore）
  const rolesSource = useMemo(
    () => (Array.isArray(roles) && roles.length ? roles : rolesLocal),
    [roles, rolesLocal]
  );

  /* ===== 役割一覧の読み込み ===== */
  useEffect(() => {
    if (Array.isArray(roles) && roles.length) {
      setRolesLocal(roles);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        setLoadingRoles(true);
        // まず 'roles' から読む。無ければ 'role' をフォールバック
        let snap = await getDocs(collection(db, "roles"));
        let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!list.length) {
          snap = await getDocs(collection(db, "role"));
          list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }
        if (!cancelled) setRolesLocal(list);
        if (!list.length) message.info("役割データがありません。先に役割を作成してください。");
      } catch (e) {
        console.error("[roles] load error:", e);
        message.error("役割一覧の取得に失敗しました。");
      } finally {
        if (!cancelled) setLoadingRoles(false);
      }
    })();

    return () => { cancelled = true; };
  }, [roles, message]);

  /* ===== 役割選択時：権限テーブル初期化 ===== */
  useEffect(() => {
    if (!selectedRoleId) {
      setPermissions({});
      return;
    }
    const selectedRole = rolesSource.find((r) => r.id === selectedRoleId);
    setLoadingRole(true);

    // 全リソース分の権限キーを必ず用意（undefined 防止）
    const full = {};
    RESOURCES.forEach((res) => {
      full[res.key] = {
        view:   !!selectedRole?.permissions?.[res.key]?.view,
        edit:   !!selectedRole?.permissions?.[res.key]?.edit,
        delete: !!selectedRole?.permissions?.[res.key]?.delete,
      };
    });

    setPermissions(full);
    setLoadingRole(false);
  }, [selectedRoleId, rolesSource]);

  /* ===== 権限変更（テーブルのチェックボックス） ===== */
  const handlePermissionChange = useCallback((resourceKey, permissionType, value) => {
    setPermissions((prev) => ({
      ...prev,
      [resourceKey]: {
        ...(prev[resourceKey] || { view: false, edit: false, delete: false }),
        [permissionType]: value,
      },
    }));
  }, []);

  /* ===== 保存（テーブル全体用の保存ボタン） ===== */
  const handleSavePermissions = useCallback(async () => {
    if (!selectedRoleId) {
      message.error("更新する役割を選択してください。");
      return;
    }
    setSaving(true);
    try {
      // 'roles' → 失敗なら 'role'
      try {
        await updateDoc(doc(db, "roles", selectedRoleId), { permissions });
      } catch {
        await updateDoc(doc(db, "role", selectedRoleId), { permissions });
      }
      setRolesLocal((prev) =>
        prev.map((r) => (r.id === selectedRoleId ? { ...r, permissions: { ...permissions } } : r))
      );
      message.success("権限を更新しました。");
    } catch (error) {
      console.error("権限更新エラー:", error);
      message.error("権限の保存中にエラーが発生しました。");
    } finally {
      setSaving(false);
    }
  }, [selectedRoleId, permissions, message]);

  /* ===== クイックトグル：オンライン一覧の閲覧可否 ===== */
  const onlineView = !!permissions.onlineList?.view;

  const handleToggleOnlineView = useCallback(async () => {
    if (!selectedRoleId) {
      message.error("まず役割を選択してください。");
      return;
    }
    const nextView = !onlineView;

    // ローカル更新
    const nextPerms = {
      ...permissions,
      onlineList: {
        view: nextView,
        edit: !!permissions.onlineList?.edit,
        delete: !!permissions.onlineList?.delete,
      },
    };
    setPermissions(nextPerms);

    // Firestore へ即反映
    setSaving(true);
    try {
      try {
        await updateDoc(doc(db, "roles", selectedRoleId), { permissions: nextPerms });
      } catch {
        await updateDoc(doc(db, "role", selectedRoleId), { permissions: nextPerms });
      }
      setRolesLocal((prev) =>
        prev.map((r) => (r.id === selectedRoleId ? { ...r, permissions: nextPerms } : r))
      );
      message.success(
        nextView
          ? "「オンライン中（一覧）」の閲覧を許可しました。"
          : "「オンライン中（一覧）」の閲覧を禁止しました。"
      );
    } catch (e) {
      console.error("toggle onlineList error:", e);
      message.error("更新に失敗しました。");
    } finally {
      setSaving(false);
    }
  }, [selectedRoleId, onlineView, permissions, message]);

  /* ===== テーブル列定義 ===== */
  const columns = useMemo(
    () => [
      { title: "機能／タブ", dataIndex: "name", key: "name", render: (t) => <Text strong>{t}</Text> },
      {
        title: "閲覧",
        key: "view",
        align: "center",
        render: (_, record) => (
          <Checkbox
            checked={permissions[record.key]?.view || false}
            onChange={(e) => handlePermissionChange(record.key, "view", e.target.checked)}
          />
        ),
      },
      {
        title: "編集／作成",
        key: "edit",
        align: "center",
        render: (_, record) => (
          <Checkbox
            checked={permissions[record.key]?.edit || false}
            onChange={(e) => handlePermissionChange(record.key, "edit", e.target.checked)}
          />
        ),
      },
      {
        title: "削除",
        key: "delete",
        align: "center",
        render: (_, record) => (
          <Checkbox
            checked={permissions[record.key]?.delete || false}
            onChange={(e) => handlePermissionChange(record.key, "delete", e.target.checked)}
          />
        ),
      },
    ],
    [permissions, handlePermissionChange]
  );

  // 役割セレクト
  const roleOptions = useMemo(
    () =>
      (rolesSource || []).map((r) => ({
        value: r.id,
        label: r.name || r.displayName || r.title || r.code || r.id,
      })),
    [rolesSource]
  );

  return (
    <div>
      {/* 役割選択 */}
      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Text>権限を設定する役割を選択してください：</Text>
        <Select
          style={{ width: 280 }}
          placeholder="役割を選択"
          options={roleOptions}
          loading={loadingRoles}
          allowClear
          value={selectedRoleId}
          onChange={(val) => setSelectedRoleId(val || null)}
        />
      </Space>

      {/* クイック操作：オンライン一覧の閲覧を一発切替 */}
      <Space style={{ marginBottom: 24, flexWrap: "wrap" }}>
        <Button onClick={handleToggleOnlineView} loading={saving}>
          {onlineView ? "オンライン一覧の閲覧を禁止する" : "オンライン一覧の閲覧を許可する"}
        </Button>
      </Space>

      {/* 権限テーブル & 保存ボタン */}
      {selectedRoleId ? (
        <>
          {loadingRole ? (
            <Spin />
          ) : (
            <Table columns={columns} dataSource={RESOURCES} rowKey="key" pagination={false} bordered />
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSavePermissions}
            style={{ marginTop: 24, float: "right" }}
            disabled={!selectedRoleId}
          >
            変更を保存
          </Button>
        </>
      ) : (
        <Text type="secondary">権限設定を開始するには、まず役割を選択してください。</Text>
      )}
    </div>
  );
};

export default PermissionManagementTab;
